import { OmsRecycleOrderDetail } from 'types/oms';
import { SelectOption } from 'types/base';
import { MallCity } from 'types/home';
import { getCityListByCountryCode } from 'apis/home';
import { SysUser } from 'types/sys';
import { DempyouType, DempyouPrintDto } from 'pages/prints/dempyou';
import dayjs from 'dayjs';
import i18n from 'i18n';
import LOCALS from 'commons/locals';
import { OMS_RECYCLE_ORDER_TYPE_MAP } from 'commons/options';
import { SelectOptionLabelList } from 'store/slices/globalInfoSlice';
import { getLocalStorageLanguage } from 'commons';
import { getShopConfig, SHOP_MAP } from 'constant/shop';

export interface ProductType {
  productName: string;
  productPrice: string | number;
  guestRemarks: string;
  type: number;
  code: string;
  productSn: string;
  brandName: string;
  productId: number;
  submissionId?: number;
  isAuthentic?: number;
}

export type PrintParamType = Pick<
  DempyouPrintDto,
  | 'code'
  | 'date'
  | 'payType'
  | 'staff'
  | 'userInfo'
  | 'memberId'
  | 'submissionId'
  | 'storeId'
  | 'isTradeIn'
> &
  Partial<
    Pick<DempyouPrintDto, 'dempyouType' | 'totalPrice' | 'payoutDetail'>
  > & {
    type: number;
  };

const getDempyouLang = (storeId?: number) => {
  if (!storeId) return getLocalStorageLanguage();

  return getShopConfig(storeId).language;
};

/** settlementType: 1-现金 2-银行转账 */
const getDempyouPayTypeLabels = (storeId?: number): string[] => {
  if (storeId === SHOP_MAP.FRANCE) {
    return ['', 'Espèces', 'Virement bancaire'];
  }

  const lang = getDempyouLang(storeId);
  return [
    '',
    i18n.t(LOCALS.iJVtIJxKRd, { lng: lang }),
    i18n.t(LOCALS.XLtWNsvkPj, { lng: lang }),
  ];
};

const getDempyouPayTypeBySettlement = (
  settlementType: number | undefined,
  storeId?: number,
) => getDempyouPayTypeLabels(storeId)[settlementType || 1] ?? '';

const getDempyouPayTypeSelectOptions = (storeId?: number) => {
  const [, cash, bank] = getDempyouPayTypeLabels(storeId);
  return [
    { value: bank, label: bank },
    { value: cash, label: cash },
  ];
};

const getDempyouProduct = (
  data: OmsRecycleOrderDetail,
  {
    name,
    rankDes,
    rankSelectOptions,
  }: {
    name?: string;
    rankDes?: string;
    rankSelectOptions: SelectOptionLabelList;
  },
): ProductType | undefined => {
  if (!data) return;
  let rankDesc = rankDes || '';

  // 这一步的目的是把 rankSelectOptions 里面的等级信息内容清除掉
  rankSelectOptions.forEach((item) => {
    rankDesc = rankDesc.replace(item.label || '', '');
    rankDesc = rankDesc.replace(item.labelEn || '', '');
    rankDesc = rankDesc.replace(item.labelJa || '', '');
    rankDesc = rankDesc.replace(item.labelTw || '', '');
    rankDesc = rankDesc.replace(item.labelCn || '', '');
  });

  const { omsRecycleOrderProduct, omsRecycleOrderItem, omsRecycleOrder } = data;
  const { productSn, brandName } = omsRecycleOrderProduct || {};
  const {
    finalSalePrice = '',
    finalRecyclePrice = '',
    guestRemarks = '',
    productId,
  } = omsRecycleOrderItem || {};
  const { type, code, submissionId, isAuthentic } = omsRecycleOrder || {};

  return {
    productName: name || '',
    productPrice:
      (type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT
        ? finalSalePrice
        : finalRecyclePrice) || '',
    productSn: productSn || '',
    brandName: brandName || '',
    guestRemarks: [rankDesc, guestRemarks].filter(Boolean).join(' ').trim(),
    type: type!,
    code: code!,
    productId: productId || 0,
    submissionId,
    isAuthentic,
  };
};

const getDempyouOrderInfo = async (
  data: OmsRecycleOrderDetail,
  {
    staffSelectOptions,
    countryOptions,
    idCertificate,
    currentStaff,
    staffName,
    payoutDetail,
  }: {
    staffSelectOptions: (SelectOption & Pick<SysUser, 'shop'>)[];
    countryOptions: (SelectOption & {
      nameI18n: {
        en: string;
        ja: string;
        zh_CN: string;
      };
    })[];
    idCertificate?: string;
    currentStaff: number;
    staffName?: string;
    payoutDetail?: string | null;
  },
): Promise<PrintParamType | undefined> => {
  const lang = getDempyouLang(data.omsRecycleOrder?.storeId);

  let cityStr:
    | { code: string; countryCode: string; id: string; name: string }
    | undefined = undefined;
  const { omsRecycleOrderLogistics, omsRecycleOrder, omsRecycleOrderItem } =
    data;
  const { code, settlementType, createBy, type, createTime, submissionId } =
    omsRecycleOrder || {};
  const isTradeIn = omsRecycleOrder?.isTradeIn;
  const { agreeFinalValuationTime } = omsRecycleOrderItem || {};
  const { name, phone, phone2, postCode, country, city, detailAddress } =
    omsRecycleOrderLogistics || {};
  const cityData = await getCityListByCountryCode(country || '', lang);
  const countryOption = countryOptions.find((d) => d.value === country);
  const countryIsJP = countryOption?.value === 'JPN';
  const countryLabel = (() => {
    if (countryIsJP) return '';
    if (!countryOption?.nameI18n) return countryOption?.label;
    // 优先展示订单对应的语言，如果订单对应的语言不存在，则展示英文，如果英文也不存在，则展示当前选择的语言
    return (
      countryOption?.nameI18n[lang as keyof typeof countryOption.nameI18n] ||
      countryOption?.nameI18n['en'] ||
      countryOption?.label
    );
  })();
  const nameStr = countryIsJP ? `${name || '-'}  様` : `${name || '-'}`;
  cityStr = cityData.data.cityList?.find((d) => d.code === city);

  const staff =
    staffSelectOptions.find(
      (d) => d.value === createBy || d.value === currentStaff,
    )?.label || '';
  const payType = getDempyouPayTypeBySettlement(
    settlementType,
    data.omsRecycleOrder?.storeId,
  );

  const userInfo = (() => {
    const phoneInfo = (() => {
      const phones = [phone, phone2].filter(Boolean);
      if (phones.length === 0) return '';

      const phoneStr = phones.map((p) => `+${p}`).join('  ');
      return data.omsRecycleOrder?.storeId === SHOP_MAP.HONGKONG
        ? `${phoneStr}        ${idCertificate || ''}`
        : phoneStr;
    })();

    const arr = [
      nameStr,
      phoneInfo,
      `${countryLabel} ${cityStr?.name || ''} ${detailAddress || ''} ${
        postCode || ''
      }`,
    ];

    return arr
      .map((i) => i?.trim())
      .filter(Boolean)
      .join('\n');
  })();

  return {
    userInfo,
    staff: staffName || staff,
    code: code || '',
    payType,
    type: type!,
    date: agreeFinalValuationTime
      ? dayjs(agreeFinalValuationTime).format('YYYY/MM/DD')
      : dayjs(createTime).format('YYYY/MM/DD'),
    memberId: data.omsRecycleOrder?.memberId || 0,
    submissionId,
    storeId: data.omsRecycleOrder?.storeId,
    isTradeIn,
    payoutDetail: payoutDetail ?? undefined,
  };
};

const getDempyouToPrint = ({
  productList,
  printParam,
  prints,
  signatureImage,
  signedContractPdf,
  omsRecycleOrderIds,
}: {
  productList: ProductType[];
  printParam: PrintParamType;
  prints?: boolean; // 是否弹窗打印
  signatureImage?: string;
  signedContractPdf?: boolean;
  omsRecycleOrderIds?: string[];
}) => {
  const payload = {
    productList,
    ...printParam,
    totalPrice: productList.reduce((total, product) => {
      const price = Number(product.productPrice) || 0;
      return total + price;
    }, 0),
    prints: signedContractPdf ? undefined : prints ? '1' : undefined,
    memberId: printParam.memberId,
    signatureImage,
  };
  const [t1, t2] =
    printParam.type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT
      ? [DempyouType.sell_customer, DempyouType.sell_store]
      : [DempyouType.recycle_customer, DempyouType.recycle_store];

  const key = `dempyou_print_${Date.now()}_${Math.random()
    .toString(36)
    .substring(2, 11)}`;

  if (signedContractPdf) {
    const isJpConsignmentPerProduct =
      printParam.type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT &&
      printParam.storeId &&
      (printParam.storeId === SHOP_MAP.GINZA ||
        printParam.storeId === SHOP_MAP.UMEDA);

    const printList: DempyouPrintDto[] = isJpConsignmentPerProduct
      ? productList.map((d) => ({
          productList: [d],
          ...printParam,
          totalPrice: d.productPrice,
          dempyouType: t1,
          code: d.code,
          memberId: printParam.memberId,
          submissionId: d.submissionId,
          signatureImage,
        }))
      : [{ ...payload, dempyouType: t1 }];

    localStorage.setItem(key, JSON.stringify(printList));
    const filename = `signed-contract-${
      printParam.submissionId || printParam.code || key
    }.pdf`;
    const orderIdsQuery = omsRecycleOrderIds?.length
      ? `&orderIds=${encodeURIComponent(omsRecycleOrderIds.join(','))}`
      : '';
    window.open(
      `/prints/dempyou?key=${key}&sendContract=1&filename=${encodeURIComponent(
        filename,
      )}${orderIdsQuery}`,
    );
    return;
  }

  let printList: DempyouPrintDto[] = [];
  // 银座寄卖类型 = 商品数 * 2 张
  if (
    printParam.type === OMS_RECYCLE_ORDER_TYPE_MAP.CONSIGNMENT &&
    printParam.storeId &&
    (printParam.storeId === SHOP_MAP.GINZA ||
      printParam.storeId === SHOP_MAP.UMEDA)
  ) {
    productList.forEach((d) => {
      printList.push({
        productList: [d],
        ...printParam,
        totalPrice: d.productPrice,
        dempyouType: t1,
        code: d.code,
        prints: '1',
        memberId: printParam.memberId,
        submissionId: d.submissionId,
      });
      printList.push({
        productList: [d],
        ...printParam,
        totalPrice: d.productPrice,
        dempyouType: t2,
        code: d.code,
        prints: '1',
        memberId: printParam.memberId,
        submissionId: d.submissionId,
      });
    });
  } else {
    printList = [
      { ...payload, dempyouType: t1 },
      { ...payload, dempyouType: t2 },
    ];
  }

  localStorage.setItem(key, JSON.stringify(printList));
  window.open(`/prints/dempyou?key=${key}`);
};

export {
  getDempyouProduct,
  getDempyouOrderInfo,
  getDempyouToPrint,
  getDempyouLang,
  getDempyouPayTypeBySettlement,
  getDempyouPayTypeSelectOptions,
};
